import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { createAmChartsRoot } from '../../utils/amChartsConfig';
import { parseLocalDateTime } from './analysisTabDateTime';
import { getSensorDataTimestamps } from './analysisTabTelemetryUtils';
import type { SensorData } from './analysisTabSensorTypes';
import { buildScaleGroups, createYAxesFromScaleGroups } from './analysisTabScaleGroups';
import { createAnalysisChartSeries } from './analysisTabCreateSeries';

const X_AXIS_DAY_PALETTE = [
  am5.color(0x2563eb),
  am5.color(0x16a34a),
  am5.color(0xdc2626),
  am5.color(0x7c3aed),
  am5.color(0xf59e0b),
  am5.color(0x0ea5e9),
  am5.color(0xfb7185),
  am5.color(0x14b8a6),
  am5.color(0xf97316),
  am5.color(0x22c55e),
  am5.color(0xef4444),
  am5.color(0x3b82f6),
  am5.color(0x84cc16),
  am5.color(0xa855f7),
  am5.color(0x06b6d4),
  am5.color(0xeab308),
  am5.color(0x9333ea),
  am5.color(0x6366f1),
  am5.color(0x10b981),
  am5.color(0xfacc15),
  am5.color(0x8b5cf6),
  am5.color(0x4ade80),
  am5.color(0xf87171),
  am5.color(0x60a5fa),
  am5.color(0xa3e635),
  am5.color(0xc084fc),
  am5.color(0x38bdf8),
  am5.color(0x34d399),
  am5.color(0xfdba74),
  am5.color(0x818cf8),
  am5.color(0x65a30d),
];

function getDayKey(date: Date): string {
  return date.toISOString().split('T')[0];
}

function resolveChartTimeBounds(
  visibleSensorsWithData: SensorData[],
  getDateRange: () => { startTime: string; endTime: string }
): { minTime: number; maxTime: number } {
  const { startTime, endTime } = getDateRange();
  let minTime = parseLocalDateTime(startTime);
  let maxTime = parseLocalDateTime(endTime);

  const allDataTimestamps = getSensorDataTimestamps(visibleSensorsWithData);
  if (allDataTimestamps.length === 0) {
    return { minTime, maxTime };
  }

  const dataMinTime = Math.min(...allDataTimestamps);
  const dataMaxTime = Math.max(...allDataTimestamps);
  const dataOutsideRequest = dataMaxTime < minTime || dataMinTime > maxTime;
  if (dataOutsideRequest) {
    minTime = dataMinTime;
    maxTime = dataMaxTime;
  }

  return { minTime, maxTime };
}

function attachXAxisDayColorLabels(xAxis: am5xy.DateAxis<am5xy.AxisRendererX>): void {
  const dayColorMap: Record<string, am5.Color> = {};
  const xRenderer = xAxis.get('renderer');
  xRenderer.labels.template.adapters.add('fill', (fill, label) => {
    const dataItem = label.dataItem;
    if (!dataItem) {
      return fill;
    }

    const value = dataItem.get('value' as never);
    if (!value) {
      return fill;
    }

    const axis = dataItem.component as am5xy.DateAxis<am5xy.AxisRendererX>;
    const dataItems = axis.dataItems;
    const currentIndex = dataItems.indexOf(dataItem);

    if (currentIndex <= 0) {
      const date = new Date(value as number);
      const key = getDayKey(date);
      if (!dayColorMap[key]) {
        dayColorMap[key] = X_AXIS_DAY_PALETTE[Object.keys(dayColorMap).length % X_AXIS_DAY_PALETTE.length];
      }
      return dayColorMap[key];
    }

    const prevDataItem = dataItems[currentIndex - 1];
    const prevValue = prevDataItem?.get('value' as never);
    if (!prevValue) {
      return am5.color(0x9ca3af);
    }

    const d1 = new Date(value as number);
    const d2 = new Date(prevValue as number);

    if (d1.getDate() !== d2.getDate()) {
      const key = getDayKey(d1);
      if (!dayColorMap[key]) {
        dayColorMap[key] = X_AXIS_DAY_PALETTE[Object.keys(dayColorMap).length % X_AXIS_DAY_PALETTE.length];
      }
      return dayColorMap[key];
    }

    return am5.color(0xffffff);
  });
}

function findYAxisForSensor(
  yAxisArray: { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[],
  normalizedUnit: string
): am5xy.ValueAxis<am5xy.AxisRendererY> | null {
  for (const entry of yAxisArray) {
    if (normalizedUnit in entry) {
      return entry[normalizedUnit];
    }
  }
  return null;
}

function appendVisibleSensorSeries(
  chart: am5xy.XYChart,
  root: am5.Root,
  visibleSensorsWithData: SensorData[],
  yAxisArray: { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[],
  xAxis: am5xy.DateAxis<am5xy.AxisRendererX>,
  cursor: am5xy.XYCursor,
  sensors: SensorData[]
): void {
  for (const sensor of visibleSensorsWithData) {
    const originalUnit = sensor.unit?.trim() || '';
    const normalizedUnit = originalUnit === '' ? sensor.sensorName : originalUnit;
    const yAxis = findYAxisForSensor(yAxisArray, normalizedUnit);
    if (!yAxis) {
      continue;
    }

    const sensorNameWithUnit = sensor.unit ? `${sensor.sensorName} (${sensor.unit})` : sensor.sensorName;
    createAnalysisChartSeries(
      chart,
      root,
      sensorNameWithUnit,
      sensor.color,
      xAxis,
      yAxis,
      sensor.dataPoints,
      cursor,
      sensors,
      sensor
    );
  }
}

function configureGroupChartLegend(chart: am5xy.XYChart, root: am5.Root): void {
  const legend = chart.children.push(
    am5.Legend.new(root, {
      x: am5.percent(50),
      centerX: am5.percent(50),
      layout: am5.GridLayout.new(root, {
        maxColumns: 6,
        fixedWidthGrid: true,
      }),
      setStateOnChildren: false,
      paddingTop: 25,
      useDefaultMarker: true,
    }),
  );

  legend.itemContainers.template.setAll({
    layout: root.horizontalLayout,
  });

  legend.markers.template.setAll({
    width: 12,
    height: 12,
    centerY: am5.p50,
    y: am5.p50,
  });

  legend.markerRectangles.template.setAll({
    cornerRadiusTL: 10,
    cornerRadiusTR: 10,
    cornerRadiusBL: 10,
    cornerRadiusBR: 10,
    width: 12,
    height: 12,
  });

  legend.labels.template.setAll({
    fill: am5.color(0xffffff),
    centerY: am5.p50,
    y: am5.p50,
  });
  legend.data.setAll(chart.series.values);
  legend.itemContainers.template.states.create('hover', {});
}

export interface BuildGroupAnalysisChartParams {
  containerId: string;
  visibleSensorsWithData: SensorData[];
  uniqueUnits: string[];
  hasDataToDisplay: boolean;
  customScaling: { [unit: string]: { min: number; max: number } } | null;
  uomRanges: { uom: string; minValue: number; maxValue: number }[];
  sensors: SensorData[];
  getDateRange: () => { startTime: string; endTime: string };
}

export function buildGroupAnalysisChart(params: BuildGroupAnalysisChartParams): {
  chart: am5xy.XYChart;
  root: am5.Root;
} {
  const root = createAmChartsRoot(params.containerId);
  root.setThemes([am5themes_Animated.new(root)]);

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panY: false,
      panX: false,
      wheelY: 'zoomX',
      layout: root.verticalLayout,
      maxTooltipDistance: 0,
      paddingTop: 50,
    }),
  );

  const { minTime, maxTime } = resolveChartTimeBounds(params.visibleSensorsWithData, params.getDateRange);

  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      maxDeviation: 0.2,
      baseInterval: { timeUnit: 'second', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: 25,
        stroke: am5.color('#FFFFFF'),
      }),
      min: minTime,
      max: maxTime,
      strictMinMax: false,
    }),
  );
  xAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));
  xAxis.set('strictMinMax', false);
  xAxis.get('renderer').labels.template.setAll({
    rotation: -45,
    centerY: am5.p100,
    centerX: am5.p100,
    paddingTop: 20,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  });

  attachXAxisDayColorLabels(xAxis as am5xy.DateAxis<am5xy.AxisRendererX>);

  const scaleGroups = params.hasDataToDisplay
    ? buildScaleGroups(params.uniqueUnits, params.visibleSensorsWithData, params.customScaling, params.uomRanges)
    : [];
  const yAxisArray = params.hasDataToDisplay
    ? createYAxesFromScaleGroups(chart, root, scaleGroups, params.visibleSensorsWithData)
    : [];

  const cursor = chart.set(
    'cursor',
    am5xy.XYCursor.new(root, {
      behavior: 'zoomXY',
    }),
  );

  cursor.selection.setAll({
    fill: am5.color(0x1fc9a7),
    fillOpacity: 0.2,
    stroke: am5.color(0x1fc9a7),
    strokeWidth: 2,
    strokeOpacity: 0.8,
  });

  cursor.lineY.set('visible', false);
  cursor.lineX.setAll({
    visible: true,
    stroke: am5.color('#ADD8E6'),
    strokeWidth: 3,
  });

  const tooltip = am5.Tooltip.new(root, {
    pointerOrientation: 'right',
    dx: -15,
  });

  tooltip.get('background')?.setAll({
    fill: am5.color('#001023'),
    stroke: am5.color('#4A5463'),
    opacity: 1,
  });

  chart.plotContainer.set('tooltipPosition', 'pointer');
  chart.plotContainer.set('tooltipHTML', '<div >' + '' + '</div>');
  chart.plotContainer.set('tooltip', tooltip);

  appendVisibleSensorSeries(
    chart,
    root,
    params.visibleSensorsWithData,
    yAxisArray,
    xAxis as am5xy.DateAxis<am5xy.AxisRendererX>,
    cursor,
    params.sensors
  );

  configureGroupChartLegend(chart, root);

  return { chart, root };
}

export function shouldBuildGroupChart(
  uniqueUnits: string[],
  selectedDataLength: number,
  hasDataToDisplay: boolean,
  hasNoData: boolean,
  chartDataLoading: boolean,
  initialDataLoadComplete: boolean
): boolean {
  return (
    uniqueUnits.length > 0 &&
    selectedDataLength > 0 &&
    hasDataToDisplay &&
    !hasNoData &&
    !chartDataLoading &&
    initialDataLoadComplete
  );
}
